import React, { useEffect, useRef, useState } from "react";

// ---------- Mock content ----------
const STEPS = [
  { key: "01", chip: "Discover", title: "Discovery call",  desc: "30 min with a partner. No sales script. We listen first, then diagnose." },
  { key: "02", chip: "Diagnose", title: "Diagnostic",      desc: "Walk the property. Map vendors, financials, and pain points across every unit." },
  { key: "03", chip: "Plan",     title: "Transition plan", desc: "30/60/90-day roadmap covering every system, every contract, every risk." },
  { key: "04", chip: "Launch",   title: "Portal go-live",  desc: "Residents and board onboarded to the portal in a weekend. Zero downtime." },
  { key: "05", chip: "Operate",  title: "Operate",         desc: "Weekly board check-ins. Monthly financials. Continuous improvement forever." },
];

const BRAND_BLUE = "#023CFA";
const BRAND_BLUE_LIGHT = "#3A6BFF";

export default function ScrollDemoMock() {
  const stageRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [subProgress, setSubProgress] = useState(0); // 0..1 within the active step

  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const el = stageRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const total = rect.height - window.innerHeight;
        const scrolled = Math.max(0, Math.min(-rect.top, total));
        const overall = total > 0 ? scrolled / total : 0;
        const raw = overall * STEPS.length;
        const idx = Math.max(0, Math.min(STEPS.length - 1, Math.floor(raw)));
        const sub = Math.max(0, Math.min(1, raw - idx));
        setActiveIdx(idx);
        setSubProgress(sub);
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(raf);
    };
  }, []);

  // Text visibility phases:
  //   0.00 → 0.15 : fade in (light → dark)
  //   0.15 → 0.75 : hold fully dark
  //   0.75 → 1.00 : fade out (dark → light)  ~25% of range for a longer, more visible fade
  // Special cases:
  //   Step 0        → skip fade-in (already visible on first paint)
  //   Last step     → skip fade-out (stays fully visible into the next section)
  let colorT; // 0 = light/invisible, 1 = fully dark
  const isLast = activeIdx === STEPS.length - 1;
  if (isLast) {
    // Only fade in (if not first), then stay dark forever.
    if (activeIdx === 0) {
      colorT = 1;
    } else {
      colorT = subProgress < 0.15 ? subProgress / 0.15 : 1;
    }
  } else if (activeIdx === 0) {
    // Already visible; only fade out at the end of its range.
    colorT = subProgress < 0.75 ? 1 : Math.max(0, 1 - (subProgress - 0.75) / 0.25);
  } else if (subProgress < 0.15) {
    colorT = subProgress / 0.15;
  } else if (subProgress > 0.75) {
    colorT = Math.max(0, 1 - (subProgress - 0.75) / 0.25);
  } else {
    colorT = 1;
  }

  const lerp = (a, b, t) => Math.round(a + (b - a) * t);
  const lerpHex = (from, to, t) => {
    const fh = from.slice(1);
    const th = to.slice(1);
    const fr = parseInt(fh.slice(0, 2), 16), fg = parseInt(fh.slice(2, 4), 16), fb = parseInt(fh.slice(4, 6), 16);
    const tr = parseInt(th.slice(0, 2), 16), tg = parseInt(th.slice(2, 4), 16), tb = parseInt(th.slice(4, 6), 16);
    return `rgb(${lerp(fr, tr, t)}, ${lerp(fg, tg, t)}, ${lerp(fb, tb, t)})`;
  };
  const LIGHT = "#D6D9DE";
  const DARK_BODY = "#0A0A0A";
  const DARK_HEAD = "#000000";
  const bodyColor = lerpHex(LIGHT, DARK_BODY, colorT);
  const headColor = lerpHex(LIGHT, DARK_HEAD, colorT);

  return (
    <main
      data-testid="scroll-demo"
      className="min-h-screen text-[#0A1428]"
      style={{ background: "#F5F5F7", fontFamily: `"SF Pro Text", -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif` }}
    >
      {/* Intro */}
      <section className="max-w-3xl mx-auto px-6 pt-24 pb-16 text-center">
        <div className="text-[11px] tracking-[0.22em] uppercase text-[#86868B] font-semibold">Scroll demo · mock</div>
        <h1
          className="mt-4 text-4xl md:text-6xl font-semibold tracking-tight"
          style={{ letterSpacing: "-0.02em", fontFamily: `"SF Pro Display", -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif` }}
        >
          Scroll to walk<br />the five-step process.
        </h1>
        <p className="mt-6 text-[15px] text-[#4B5563] max-w-lg mx-auto leading-relaxed">
          Each block activates as it enters the frame — text reveals top-to-bottom, then folds away as the next block takes over.
        </p>
        <div className="mt-10 text-[12px] tracking-widest uppercase text-[#86868B] animate-bounce">↓ scroll</div>
      </section>

      {/* Scroll stage — one step per viewport-height of scroll */}
      <section
        ref={stageRef}
        style={{ height: `${STEPS.length * 100}vh` }}
        className="relative"
      >
        <div className="sticky top-0 h-screen flex flex-col items-center justify-center px-6">
          {/* Chips row */}
          <div className="flex gap-3 md:gap-4 mb-12" data-testid="chips-row">
            {STEPS.map((s, i) => {
              const isActive = i === activeIdx;
              return (
                <div
                  key={s.key}
                  data-testid={`chip-${i}`}
                  aria-current={isActive}
                  className="rounded-2xl flex flex-col items-center justify-center px-3"
                  style={{
                    width: 84,
                    height: 84,
                    background: isActive
                      ? // Apple-style radial + linear blend using brand blue
                        `radial-gradient(120% 90% at 20% 15%, #6B8FFF 0%, #3A6BFF 35%, #023CFA 75%, #001FB0 100%)`
                      : "#FFFFFF",
                    color: isActive ? "#FFFFFF" : "#0A0A0A",
                    border: isActive ? "1px solid rgba(2,60,250,0.4)" : "1px solid #E5E7EB",
                    boxShadow: isActive
                      ? "0 10px 28px rgba(2, 60, 250, 0.28), inset 0 1px 0 rgba(255,255,255,0.25)"
                      : "0 1px 2px rgba(0,0,0,0.04)",
                    transition:
                      "background 600ms cubic-bezier(0.22, 1, 0.36, 1), color 400ms ease, box-shadow 500ms ease, border-color 400ms ease",
                  }}
                >
                  <span
                    className="text-[9px] tracking-[0.22em] uppercase"
                    style={{ opacity: 0.75, fontWeight: 600 }}
                  >
                    {s.key}
                  </span>
                  <span className="text-[12px] mt-1.5 font-semibold leading-none">{s.chip}</span>
                </div>
              );
            })}
          </div>

          {/* Description panel */}
          <div className="text-center max-w-xl" data-testid="desc-panel">
            <h2
              className="text-3xl md:text-4xl font-semibold"
              style={{
                letterSpacing: "-0.02em",
                color: headColor,
                transition: "color 700ms cubic-bezier(0.22, 1, 0.36, 1)",
                fontFamily: `"SF Pro Display", -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif`,
              }}
            >
              {STEPS[activeIdx].title}
            </h2>
            <p
              className="mt-5 text-[18px] md:text-[20px] leading-relaxed"
              style={{
                color: bodyColor,
                transition: "color 700ms cubic-bezier(0.22, 1, 0.36, 1)",
              }}
              data-testid="desc-text"
            >
              {STEPS[activeIdx].desc}
            </p>
          </div>
        </div>
      </section>

      {/* Outro */}
      <section className="max-w-3xl mx-auto px-6 py-24 text-center">
        <div className="text-[11px] tracking-[0.22em] uppercase text-[#86868B]">end of demo</div>
        <p className="mt-4 text-[15px] text-[#4B5563]">Scroll back up to reverse.</p>
      </section>
    </main>
  );
}
